import { api, ApiError, OfflineError } from './api.ts';
import { store } from './db.ts';
import {
  applyRemote,
  encodeState,
  sealUpdate,
  setOutgoingHandler,
  withDoc,
} from './docs.ts';
import { emptyNote } from './notes.ts';
import { openWithVault, sealWithVault } from '../crypto/session.ts';
import { pushPendingVersions } from './versions.ts';
import type { ClientMessage, SyncMessage, VaultIndexContent } from '../../../shared/protocol.ts';

/**
 * Abgleich ab Phase 3: verschlüsselte CRDT-Deltas über einen WebSocket, der Server ist
 * ein dummer Briefkasten. Es gibt keinen Konfliktfall mehr — zwei Geräte, die offline
 * dieselbe Notiz ändern, werden beim Wiedersehen zeichenweise zusammengeführt.
 *
 * Der WebSocket kann Nachrichten verpassen, deshalb liegt darunter immer noch ein
 * Nachholweg über HTTP.
 */

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error';

let state: SyncState = 'offline';
let pending = 0;
let socket: WebSocket | null = null;
let deviceId = '';
let reconnectDelay = 1_000;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let stopped = true;

const listeners = new Set<() => void>();

export function onSyncChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(next?: SyncState): void {
  if (next) state = next;
  for (const listener of listeners) listener();
}

export function syncStatus(): { state: SyncState; pending: number } {
  return { state, pending };
}

async function refreshPending(): Promise<void> {
  pending = (await store.outbox()).length;
}

// --- ausgehende Deltas -----------------------------------------------------

setOutgoingHandler((noteId, update) => {
  void enqueue(noteId, update);
});

async function enqueue(noteId: string, update: Uint8Array): Promise<void> {
  const note = await store.getNote(noteId);
  if (!note) return;
  const entry = {
    ref: crypto.randomUUID(),
    noteId,
    ciphertext: sealUpdate(update, note.wrappedDek),
    createdAt: Date.now(),
  };
  await store.queueUpdate(entry);
  await refreshPending();
  emit();
  send({ t: 'u', noteId, ref: entry.ref, ciphertext: entry.ciphertext });
}

function send(message: ClientMessage): boolean {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false;
  socket.send(JSON.stringify(message));
  return true;
}

/**
 * Was in der Warteschlange liegt, geht über den WebSocket oder — wenn der gerade nicht
 * steht — über HTTP. Doppelt gesendete Deltas sind harmlos: CRDT-Updates sind
 * idempotent.
 */
async function flushOutbox(): Promise<void> {
  for (const entry of await store.outbox()) {
    if (send({ t: 'u', noteId: entry.noteId, ref: entry.ref, ciphertext: entry.ciphertext })) {
      continue;
    }
    try {
      const result = await api.postUpdate(entry.noteId, entry.ciphertext, deviceId);
      await onAccepted(entry.noteId, result.seq);
      await store.dequeue(entry.ref);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        // Die Zeile fehlt noch auf dem Server; sie wird gleich angelegt.
        return;
      }
      throw error;
    }
  }
  await refreshPending();
  emit();
}

async function onAccepted(noteId: string, seq: number): Promise<void> {
  await store.patchNote(noteId, (note) => ({
    ...note,
    seq: Math.max(note.seq, seq),
    sinceSnapshot: note.sinceSnapshot + 1,
  }));
}

// --- eingehende Deltas -----------------------------------------------------

async function receive(noteId: string, seq: number, ciphertext: string): Promise<void> {
  let note = await store.getNote(noteId);
  if (!note) {
    // Notiz von einem anderen Gerät, die wir noch nicht kennen: erst die Zeile holen.
    await pullNotes();
    note = await store.getNote(noteId);
    // Hier lag ein Datenverlust: Das Delta wurde verworfen, die Zählmarke rückte aber
    // trotzdem vor (`catchUp` schreibt `maxSeq`). Damit war dieser Stand für dieses
    // Gerät verloren — sichtbar wurde die Notiz erst beim nächsten vollen Abgleich,
    // also bis zu eine Minute später und im ungünstigen Fall ohne den Text.
    if (!note) return;
  }
  await applyRemote(note, ciphertext);
  await store.patchNote(noteId, (current) => ({
    ...current,
    seq: Math.max(current.seq, seq),
    sinceSnapshot: current.sinceSnapshot + 1,
  }));
  await store.setLastSeq(Math.max(await store.getLastSeq(), seq));
}

async function catchUp(): Promise<void> {
  for (let round = 0; round < 20; round += 1) {
    const since = await store.getLastSeq();
    const response = await api.catchUp(since);
    for (const update of response.updates) {
      await receive(update.noteId, update.seq, update.ciphertext).catch((error) =>
        console.warn('[sync] Update nicht anwendbar', update.noteId, error),
      );
    }
    await store.setLastSeq(Math.max(since, response.maxSeq));
    if (!response.more) return;
  }
}

// --- Zeilen (anlegen, löschen, wiederherstellen) ---------------------------

async function pushRows(): Promise<void> {
  for (const note of await store.dirtyNotes()) {
    if (note.deletedAt !== null) {
      if (note.seq === 0 && note.state === null) {
        await store.removeNote(note.id);
        continue;
      }
      await api.deleteNote(note.id).catch((error) => {
        if (!(error instanceof ApiError && error.status === 404)) throw error;
      });
    } else {
      await api.putNote(note.id, { wrappedDek: note.wrappedDek, updatedAt: note.updatedAt });
    }
    await store.patchNote(note.id, (current) => ({ ...current, dirty: false }));
  }
}

/**
 * Stellt sicher, dass die Zeile einer Notiz auf dem Server existiert. Anhänge hängen
 * an ihr — wer eine frische Notiz anlegt und sofort ein Bild einfügt, käme sonst gegen
 * eine unbekannte Notiz.
 */
export async function ensureNoteOnServer(noteId: string): Promise<void> {
  const note = await store.getNote(noteId);
  if (!note || note.deletedAt !== null) return;
  if (!note.dirty && note.seq > 0) return;
  await api.putNote(noteId, { wrappedDek: note.wrappedDek, updatedAt: note.updatedAt });
  await store.patchNote(noteId, (current) => ({ ...current, dirty: false }));
}

async function pullNotes(): Promise<void> {
  const since = await store.getLastPull();
  const response = await api.listNotes(since);
  let high = since;

  for (const row of response.notes) {
    high = Math.max(high, row.updatedAt);
    const local = await store.getNote(row.id);

    if (!local) {
      await store.putNote({
        ...emptyNote(row.id, row.wrappedDek, row.updatedAt),
        deletedAt: row.deletedAt,
      });
      await fetchState(row.id);
      continue;
    }
    if (local.dirty) continue; // eigene Änderung an der Zeile hat Vorrang
    if (local.deletedAt !== row.deletedAt) {
      await store.patchNote(row.id, (current) => ({
        ...current,
        deletedAt: row.deletedAt,
        updatedAt: row.updatedAt,
      }));
    }
    if (local.state === null && row.snapshotSeq > 0) await fetchState(row.id);
  }
  await store.setLastPull(high);
}

/** Snapshot und alle Deltas darüber — der Weg, wie ein frisches Gerät eine Notiz aufbaut. */
async function fetchState(noteId: string): Promise<void> {
  const note = await store.getNote(noteId);
  if (!note) return;
  const response = await api.noteState(noteId, note.seq);
  let seq = note.seq;

  if (response.snapshot) {
    await applyRemote(note, response.snapshot.ciphertext);
    seq = Math.max(seq, response.snapshot.seq);
  }
  for (const update of response.updates) {
    const current = await store.getNote(noteId);
    if (!current) return;
    await applyRemote(current, update.ciphertext);
    seq = Math.max(seq, update.seq);
  }
  await store.patchNote(noteId, (current) => ({ ...current, seq: Math.max(current.seq, seq) }));
}

// --- Kompaktierung ---------------------------------------------------------

const COMPACT_AFTER = 200;

async function compact(): Promise<void> {
  for (const note of await store.allNotes()) {
    if (note.sinceSnapshot < COMPACT_AFTER || note.seq === 0) continue;
    const ciphertext = await withDoc(note.id, (entry) =>
      sealUpdate(encodeState(entry), note.wrappedDek),
    );
    await api.putSnapshot(note.id, ciphertext, note.seq);
    await store.patchNote(note.id, (current) => ({ ...current, sinceSnapshot: 0 }));
  }
}

// --- Ordner und Reihenfolge ------------------------------------------------

let indexDirty = false;

export function markIndexDirty(): void {
  indexDirty = true;
}

async function syncIndex(): Promise<void> {
  const local = await store.getIndex();
  if (indexDirty) {
    await api.putIndex(sealWithVault(new TextEncoder().encode(JSON.stringify(local))), Date.now());
    indexDirty = false;
    return;
  }
  const remote = await api.getIndex();
  if (!remote) return;
  const decoded = JSON.parse(
    new TextDecoder().decode(openWithVault(remote.payload)),
  ) as VaultIndexContent;
  await store.setIndex(decoded);
}

// --- WebSocket -------------------------------------------------------------

function connect(token: string): void {
  if (stopped) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  const url = new URL('/api/sync', location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);
  url.searchParams.set('device', deviceId);

  const ws = new WebSocket(url);
  socket = ws;

  ws.addEventListener('open', () => {
    reconnectDelay = 1_000;
    emit('idle');
    void syncNow();
  });

  ws.addEventListener('message', (event) => {
    let message: SyncMessage;
    try {
      message = JSON.parse(String(event.data)) as SyncMessage;
    } catch {
      return;
    }
    void (async () => {
      switch (message.t) {
        case 'u':
          await receive(message.noteId, message.seq, message.ciphertext);
          emit();
          break;
        case 'ack':
          await store.dequeue(message.ref);
          await refreshPending();
          emit();
          break;
        case 'err':
          if (message.reason === 'unknown_note') await pushRows().catch(() => undefined);
          break;
        case 'sync':
          await pullNotes().catch(() => undefined);
          emit();
          break;
        default:
          break;
      }
    })();
  });

  const drop = (): void => {
    if (socket === ws) socket = null;
    if (stopped) return;
    emit('offline');
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => void openSocket(), reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
  };
  ws.addEventListener('close', drop);
  ws.addEventListener('error', drop);
}

async function openSocket(): Promise<void> {
  const auth = await store.getAuth();
  if (auth) connect(auth.token);
}

// --- Ablauf ----------------------------------------------------------------

let running = false;

export async function syncNow(): Promise<void> {
  if (running || stopped) return;
  running = true;
  emit('syncing');
  try {
    await pushRows();
    await flushOutbox();
    await pullNotes();
    await catchUp();
    await syncIndex();
    await pushPendingVersions();
    await compact();
    await refreshPending();
    emit('idle');
  } catch (error) {
    if (error instanceof OfflineError) emit('offline');
    else {
      console.error('[sync]', error);
      emit('error');
    }
  } finally {
    running = false;
    emit();
  }
}

/**
 * Eine Minute war zu lang für den Fall, dass die Verbindung einen Stand verpasst
 * hat. Zwanzig Sekunden kosten eine leere Antwort und begrenzen den Schaden.
 */
export function startSync(intervalMs = 20_000): void {
  stopped = false;
  void (async () => {
    deviceId = await store.getDeviceId();
    await refreshPending();
    await openSocket();
    void syncNow();
  })();

  if (timer) clearInterval(timer);
  timer = setInterval(() => {
    void openSocket();
    void syncNow();
  }, intervalMs);

  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('online', onWake);
}

function onWake(): void {
  if (document.visibilityState === 'hidden') return;
  void openSocket();
  void syncNow();
}

export function stopSync(): void {
  stopped = true;
  if (timer) clearInterval(timer);
  if (reconnectTimer) clearTimeout(reconnectTimer);
  timer = null;
  reconnectTimer = null;
  socket?.close();
  socket = null;
  document.removeEventListener('visibilitychange', onWake);
  window.removeEventListener('online', onWake);
  emit('offline');
}
